import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  User,
  TrendingUp,
  TrendingDown,
  Minus,
  Play,
  Pause,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart3,
  MessageSquare,
  Target,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  Calendar,
  Download,
  Settings,
  Copy,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CallHistoryPage = () => {
  const navigate = useNavigate();
  const { user: authUser, token } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedCall, setExpandedCall] = useState(null);
  const [analyzingCall, setAnalyzingCall] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d');
  
  // Check authentication
  useEffect(() => {
    if (!token || !authUser) {
      navigate('/login');
      return;
    }
    
    if (!['principal', 'business_head', 'rm', 'dealer'].includes(authUser.role)) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    
    setUser(authUser);
  }, [navigate, token, authUser]);
  
  // Fetch call logs
  const fetchCalls = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      let url = `${API}/api/calls/logs?limit=100`;
      
      if (sentimentFilter !== 'all') {
        url += `&sentiment=${sentimentFilter}`;
      }
      
      // Calculate date range
      const now = new Date();
      let fromDate = null;
      
      switch (dateRange) {
        case '24h':
          fromDate = new Date(now - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          fromDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          fromDate = new Date(now - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }
      
      if (fromDate) {
        url += `&from_date=${fromDate.toISOString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalls(data);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
    }
  }, [sentimentFilter, dateRange, token]);
  
  // Fetch call statistics
  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/calls/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);
  
  // Fetch webhook configuration
  const fetchWebhookConfig = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/exotel/webhook/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWebhookConfig(data);
      }
    } catch (error) {
      console.error('Error fetching webhook config:', error);
    }
  }, [token]);
  
  // Create test call
  const createTestCall = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/exotel/test-webhook`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Test call log created');
        fetchCalls();
      }
    } catch (error) {
      toast.error('Failed to create test call');
    }
  };
  
  useEffect(() => {
    if (user) {
      fetchCalls();
      if (['principal', 'business_head'].includes(user.role)) {
        fetchStats();
      }
      if (user.role === 'principal') {
        fetchWebhookConfig();
      }
    }
  }, [user, fetchCalls, fetchStats, fetchWebhookConfig]);
  
  // Analyze a call
  const analyzeCall = async (call) => {
    if (!token) return;
    
    try {
      setAnalyzingCall(call.call_id);
      
      const response = await fetch(`${API}/api/calls/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          call_id: call.call_id,
          recording_url: call.recording_url,
          client_code: call.client_code
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Call analysis complete');
        // Refresh to show updated data
        fetchCalls();
      } else {
        toast.error(data.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze call');
    } finally {
      setAnalyzingCall(null);
    }
  };
  
  // Get sentiment icon and color
  const getSentimentDisplay = (sentiment, score) => {
    switch (sentiment) {
      case 'positive':
        return {
          icon: <TrendingUp className="w-4 h-4" />,
          color: 'bg-green-500/20 text-green-400 border-green-500/30',
          label: 'Positive'
        };
      case 'negative':
        return {
          icon: <TrendingDown className="w-4 h-4" />,
          color: 'bg-red-500/20 text-red-400 border-red-500/30',
          label: 'Negative'
        };
      default:
        return {
          icon: <Minus className="w-4 h-4" />,
          color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
          label: 'Neutral'
        };
    }
  };
  
  // Get call direction icon
  const getCallIcon = (direction, status) => {
    if (status === 'missed' || status === 'no-answer') {
      return <PhoneMissed className="w-4 h-4 text-red-400" />;
    }
    if (direction === 'inbound') {
      return <PhoneIncoming className="w-4 h-4 text-blue-400" />;
    }
    return <PhoneOutgoing className="w-4 h-4 text-green-400" />;
  };
  
  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Filter calls by search
  const filteredCalls = calls.filter(call => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      call.client_name?.toLowerCase().includes(query) ||
      call.client_code?.toLowerCase().includes(query) ||
      call.phone?.includes(query) ||
      call.summary?.toLowerCase().includes(query)
    );
  });
  
  if (!user) return null;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Phone className="w-8 h-8 text-emerald-400" />
              Call History & Analysis
            </h1>
            <p className="text-slate-400 mt-1">
              View call recordings, transcriptions, and AI sentiment analysis
            </p>
          </div>
          
          <Button
            onClick={() => { fetchCalls(); fetchStats(); }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Stats Cards (Principal/Business Head only) */}
      {stats && ['principal', 'business_head'].includes(user.role) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Calls</p>
                  <p className="text-2xl font-bold text-white">{stats.total_calls}</p>
                </div>
                <Phone className="w-8 h-8 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Analyzed</p>
                  <p className="text-2xl font-bold text-white">{stats.total_analyzed}</p>
                  <p className="text-xs text-slate-500">{stats.analysis_coverage}% coverage</p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Positive</p>
                  <p className="text-2xl font-bold text-green-400">
                    {stats.sentiment_distribution?.positive?.count || 0}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Negative</p>
                  <p className="text-2xl font-bold text-red-400">
                    {stats.sentiment_distribution?.negative?.count || 0}
                  </p>
                </div>
                <TrendingDown className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Follow-ups</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.follow_up_required}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-yellow-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Auto-Analysis Configuration (Principal only) */}
      {user.role === 'principal' && webhookConfig && (
        <Card className="bg-gradient-to-r from-emerald-900/30 to-slate-800/50 border-emerald-500/30 mb-8">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium flex items-center gap-2">
                    Automatic Call Analysis
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      {webhookConfig.auto_analysis?.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Calls longer than 30 seconds are automatically analyzed using AI
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={createTestCall}
                >
                  Create Test Call
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={() => setShowWebhookConfig(!showWebhookConfig)}
                >
                  <Settings className="w-4 h-4 mr-1" />
                  {showWebhookConfig ? 'Hide' : 'Setup'}
                </Button>
              </div>
            </div>
            
            {showWebhookConfig && (
              <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Webhook URL (for Exotel Dashboard)</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-900 text-emerald-400 px-3 py-2 rounded text-sm font-mono overflow-x-auto">
                      {webhookConfig.webhook_url}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(webhookConfig.webhook_url);
                        toast.success('Webhook URL copied!');
                      }}
                      className="text-slate-400 hover:text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Setup Instructions</label>
                  <ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside bg-slate-900/50 p-3 rounded">
                    {webhookConfig.instructions?.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by client name, code, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-600 text-white"
          />
        </div>
        
        <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
          <SelectTrigger className="w-[150px] bg-slate-800 border-slate-600 text-white">
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-600">
            <SelectItem value="all">All Sentiments</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="negative">Negative</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[150px] bg-slate-800 border-slate-600 text-white">
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-600">
            <SelectItem value="24h">Last 24 Hours</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Call List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : filteredCalls.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center">
              <Phone className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No calls found</p>
              <p className="text-slate-500 text-sm">Try adjusting your filters</p>
            </CardContent>
          </Card>
        ) : (
          filteredCalls.map((call) => {
            const isExpanded = expandedCall === call.call_id;
            const sentimentDisplay = getSentimentDisplay(call.sentiment, call.sentiment_score);
            
            return (
              <Card
                key={call.call_id}
                className={`bg-slate-800/50 border-slate-700 transition-all ${
                  isExpanded ? 'ring-1 ring-emerald-500/50' : ''
                }`}
              >
                <CardContent className="p-4">
                  {/* Main Row */}
                  <div className="flex items-center gap-4">
                    {/* Call Icon */}
                    <div className="flex-shrink-0">
                      {getCallIcon(call.direction, call.status)}
                    </div>
                    
                    {/* Client Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium truncate">
                          {call.client_name || 'Unknown'}
                        </p>
                        {call.client_code && (
                          <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                            {call.client_code}
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm">{call.phone}</p>
                    </div>
                    
                    {/* Duration */}
                    <div className="flex items-center gap-1 text-slate-400">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{formatDuration(call.duration)}</span>
                    </div>
                    
                    {/* Sentiment Badge */}
                    {call.sentiment && (
                      <Badge className={`${sentimentDisplay.color} border`}>
                        {sentimentDisplay.icon}
                        <span className="ml-1">{sentimentDisplay.label}</span>
                      </Badge>
                    )}
                    
                    {/* Date/Time */}
                    <div className="text-right text-sm text-slate-400 min-w-[100px]">
                      {call.call_time ? new Date(call.call_time).toLocaleDateString() : 'N/A'}
                      <br />
                      <span className="text-xs">
                        {call.call_time ? new Date(call.call_time).toLocaleTimeString() : ''}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {call.recording_url && !call.analysis_complete && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => analyzeCall(call)}
                          disabled={analyzingCall === call.call_id}
                        >
                          {analyzingCall === call.call_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <BarChart3 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedCall(isExpanded ? null : call.call_id)}
                        className="text-slate-400 hover:text-white"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
                      {/* Summary */}
                      {call.summary && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Summary
                          </h4>
                          <p className="text-slate-400 text-sm bg-slate-900/50 p-3 rounded">
                            {call.summary}
                          </p>
                        </div>
                      )}
                      
                      {/* Key Topics & Action Items */}
                      <div className="grid grid-cols-2 gap-4">
                        {call.key_topics && call.key_topics.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4" />
                              Key Topics
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {call.key_topics.map((topic, i) => (
                                <Badge key={i} variant="secondary" className="bg-slate-700 text-slate-300">
                                  {topic}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {call.action_items && call.action_items.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Action Items
                            </h4>
                            <ul className="text-sm text-slate-400 space-y-1">
                              {call.action_items.map((item, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-emerald-400">•</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {/* Audio Player */}
                      {call.recording_url && (
                        <div className="flex items-center gap-4 bg-slate-900/50 p-3 rounded">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-600"
                            onClick={() => setPlayingAudio(playingAudio === call.call_id ? null : call.call_id)}
                          >
                            {playingAudio === call.call_id ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                          
                          <div className="flex-1">
                            {playingAudio === call.call_id ? (
                              <audio
                                src={call.recording_url}
                                controls
                                autoPlay
                                className="w-full h-8"
                                onEnded={() => setPlayingAudio(null)}
                              />
                            ) : (
                              <div className="h-8 bg-slate-800 rounded flex items-center px-3">
                                <span className="text-slate-500 text-sm">Click play to listen</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Follow-up Alert */}
                      {call.follow_up_required && (
                        <div className="flex items-start gap-3 bg-yellow-500/10 border border-yellow-500/30 p-3 rounded">
                          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-yellow-400 font-medium">Follow-up Required</p>
                            <p className="text-slate-400 text-sm">{call.follow_up_reason || 'Please follow up with the client'}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Not analyzed message */}
                      {!call.analysis_complete && call.recording_url && (
                        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-2">
                          <FileText className="w-4 h-4" />
                          <span>Call not yet analyzed. Click the analyze button to generate insights.</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      
      {/* Common Topics (Principal/Business Head) */}
      {stats && stats.common_topics && stats.common_topics.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700 mt-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              Most Discussed Topics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.common_topics.map((t, i) => (
                <Badge
                  key={i}
                  className="bg-slate-700 text-slate-300 border-slate-600"
                >
                  {t.topic}
                  <span className="ml-2 text-emerald-400">({t.count})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CallHistoryPage;
